// Flight times must be shown in the **departure airport's local time**, not the
// operator's browser timezone. An operator in UTC-3 watching a MIA disruption was
// shown LA1341 as "Jun 4, 12:35 AM" when it actually departs MIA at "Jun 3, 11:35 PM"
// — which also made the overnight/hotel logic look wrong.
//
// IMPORTANT: This module now uses IANA timezone identifiers for DST-aware formatting.
// Issue #612: Fixed offsets caused 1-hour errors during daylight saving time transitions.
// The AIRPORT_IANA_ZONE map handles DST automatically via Intl.DateTimeFormat.
//
// For backwards compatibility, `airportUtcOffset` still returns the STANDARD offset
// (winter time for southern hemisphere, summer time for northern). This is used by
// legacy code that needs a numeric offset, but new code should use the format functions.

/** IANA timezone identifier for each airport. Handles DST transitions automatically. */
const AIRPORT_IANA_ZONE: Record<string, string> = {
  // Chile — America/Santiago observes DST (UTC-3 summer Sep–Apr, UTC-4 winter Apr–Sep)
  SCL: 'America/Santiago',
  ANF: 'America/Santiago',
  CJC: 'America/Santiago',
  IQQ: 'America/Santiago',
  LSC: 'America/Santiago',
  CCP: 'America/Santiago',
  PMC: 'America/Santiago',
  // Magallanes region (Punta Arenas, Balmaceda) — permanent UTC-3, no DST
  PUQ: 'America/Punta_Arenas',
  BBA: 'America/Punta_Arenas',
  // Easter Island — Pacific/Easter (UTC-5 summer, UTC-6 winter)
  IPC: 'Pacific/Easter',
  // Chilean regional stations flown by SKY (H2)
  ARI: 'America/Santiago',
  CPO: 'America/Santiago',
  ZCO: 'America/Santiago',
  ZAL: 'America/Santiago',
  ZOS: 'America/Santiago',

  // Peru — no DST, always UTC-5
  LIM: 'America/Lima',
  CUZ: 'America/Lima',
  AQP: 'America/Lima',
  TRU: 'America/Lima',
  PIU: 'America/Lima',
  IQT: 'America/Lima',

  // Brazil — no DST since 2019, always UTC-3 (except Manaus UTC-4)
  GRU: 'America/Sao_Paulo',
  CGH: 'America/Sao_Paulo',
  GIG: 'America/Sao_Paulo',
  SDU: 'America/Sao_Paulo',
  BSB: 'America/Sao_Paulo',
  CNF: 'America/Sao_Paulo',
  SSA: 'America/Sao_Paulo',
  REC: 'America/Sao_Paulo',
  FOR: 'America/Fortaleza',
  POA: 'America/Sao_Paulo',
  CWB: 'America/Sao_Paulo',
  FLN: 'America/Sao_Paulo',
  NAT: 'America/Fortaleza',
  MAO: 'America/Manaus',

  // Colombia — no DST, always UTC-5
  BOG: 'America/Bogota',
  MDE: 'America/Bogota',
  CLO: 'America/Bogota',
  CTG: 'America/Bogota',
  BAQ: 'America/Bogota',

  // Argentina — no DST, always UTC-3
  EZE: 'America/Argentina/Buenos_Aires',
  AEP: 'America/Argentina/Buenos_Aires',
  COR: 'America/Argentina/Cordoba',
  MDZ: 'America/Argentina/Mendoza',
  BRC: 'America/Argentina/Rio_Gallegos',

  // Uruguay — no DST since 2015, always UTC-3
  MVD: 'America/Montevideo',

  // Paraguay — observes DST (UTC-3 summer Oct–Mar, UTC-4 winter Mar–Oct)
  ASU: 'America/Asuncion',

  // Ecuador — no DST, always UTC-5
  UIO: 'America/Guayaquil',
  GYE: 'America/Guayaquil',

  // Venezuela — no DST, always UTC-4
  CCS: 'America/Caracas',
  VLN: 'America/Caracas',

  // Europe — observes DST (UTC+1/+2 for Western, UTC+2/+3 for Eastern)
  MAD: 'Europe/Madrid',
  BCN: 'Europe/Madrid',
  AGP: 'Europe/Madrid',
  PMI: 'Europe/Madrid',
  SVQ: 'Europe/Madrid',
  VLC: 'Europe/Madrid',
  BIO: 'Europe/Madrid',
  ALC: 'Europe/Madrid',
  SCQ: 'Europe/Madrid',
  VGO: 'Europe/Madrid',
  IBZ: 'Europe/Madrid',
  // Canary Islands — Atlantic/Canary (UTC+0 winter, UTC+1 summer)
  LPA: 'Atlantic/Canary',
  TFN: 'Atlantic/Canary',
  ACE: 'Atlantic/Canary',

  LIS: 'Europe/Lisbon',
  OPO: 'Europe/Lisbon',

  FCO: 'Europe/Rome',
  MXP: 'Europe/Rome',
  NAP: 'Europe/Rome',

  LHR: 'Europe/London',
  LGW: 'Europe/London',
  MAN: 'Europe/London',
  EDI: 'Europe/London',

  DUB: 'Europe/Dublin',

  CDG: 'Europe/Paris',
  ORY: 'Europe/Paris',

  FRA: 'Europe/Berlin',
  MUC: 'Europe/Berlin',
  DUS: 'Europe/Berlin',
  BER: 'Europe/Berlin',

  AMS: 'Europe/Amsterdam',
  BRU: 'Europe/Brussels',
  ZRH: 'Europe/Zurich',
  GVA: 'Europe/Zurich',
  VIE: 'Europe/Vienna',
  PRG: 'Europe/Prague',
  WAW: 'Europe/Warsaw',
  CPH: 'Europe/Copenhagen',
  ARN: 'Europe/Stockholm',
  ATH: 'Europe/Athens',
  IST: 'Europe/Istanbul',

  // North America — observes DST (except Arizona, Hawaii, Puerto Rico)
  // US East Coast (UTC-5 winter, UTC-4 summer)
  JFK: 'America/New_York',
  LGA: 'America/New_York',
  EWR: 'America/New_York',
  BOS: 'America/New_York',
  PHL: 'America/New_York',
  IAD: 'America/New_York',
  DCA: 'America/New_York',
  BWI: 'America/New_York',
  CLT: 'America/New_York',
  ATL: 'America/New_York',
  MIA: 'America/New_York',
  FLL: 'America/New_York',
  TPA: 'America/New_York',
  MCO: 'America/New_York',
  JAX: 'America/New_York',
  RSW: 'America/New_York',
  PBI: 'America/New_York',
  RDU: 'America/New_York',
  DTW: 'America/Detroit',
  CLE: 'America/New_York',
  PIT: 'America/New_York',
  CMH: 'America/New_York',
  IND: 'America/Indiana/Indianapolis',
  PVD: 'America/New_York',
  BDL: 'America/New_York',

  // US Central (UTC-6 winter, UTC-5 summer)
  ORD: 'America/Chicago',
  MDW: 'America/Chicago',
  DFW: 'America/Chicago',
  DAL: 'America/Chicago',
  HOU: 'America/Chicago',
  IAH: 'America/Chicago',
  AUS: 'America/Chicago',
  SAT: 'America/Chicago',
  MSY: 'America/Chicago',
  STL: 'America/Chicago',
  MCI: 'America/Chicago',
  OKC: 'America/Chicago',
  BNA: 'America/Chicago',
  MSP: 'America/Chicago',
  MKE: 'America/Chicago',

  // US Mountain (UTC-7 winter, UTC-6 summer, except Arizona)
  DEN: 'America/Denver',
  SLC: 'America/Denver',
  ABQ: 'America/Denver',
  ELP: 'America/Denver',
  // Arizona — no DST, always UTC-7
  PHX: 'America/Phoenix',

  // US Pacific (UTC-8 winter, UTC-7 summer)
  LAX: 'America/Los_Angeles',
  SFO: 'America/Los_Angeles',
  SAN: 'America/Los_Angeles',
  SJC: 'America/Los_Angeles',
  OAK: 'America/Los_Angeles',
  SMF: 'America/Los_Angeles',
  BUR: 'America/Los_Angeles',
  ONT: 'America/Los_Angeles',
  SNA: 'America/Los_Angeles',
  LAS: 'America/Los_Angeles',
  SEA: 'America/Los_Angeles',
  PDX: 'America/Los_Angeles',
  RNO: 'America/Los_Angeles',
  // California outstations
  HHR: 'America/Los_Angeles',
  CLD: 'America/Los_Angeles',
  CEC: 'America/Los_Angeles',
  MMH: 'America/Los_Angeles',
  // New Mexico outstations
  AXX: 'America/Denver',
  SVC: 'America/Denver',
  GUP: 'America/Denver',

  // Hawaii — no DST, always UTC-10
  HNL: 'Pacific/Honolulu',
  OGG: 'Pacific/Honolulu',

  // Puerto Rico — no DST, always UTC-4
  SJU: 'America/Puerto_Rico',

  // Canada
  YYZ: 'America/Toronto',
  YVR: 'America/Vancouver',
  YUL: 'America/Toronto',

  // Mexico — no DST since 2022 for most areas
  MEX: 'America/Mexico_City',
  MTY: 'America/Monterrey',
  CUN: 'America/Cancun',
  SJD: 'America/Mazatlan',
  PVR: 'America/Mexico_City',

  // Central America & Caribbean
  PTY: 'America/Panama',
  SJO: 'America/Costa_Rica',
  GUA: 'America/Guatemala',
  SAL: 'America/El_Salvador',
  HAV: 'America/Havana',
  SDQ: 'America/Santo_Domingo',
  MBJ: 'America/Jamaica',
  NAS: 'America/Nassau',

  // Middle East — mostly no DST
  AUH: 'Asia/Dubai',
  DXB: 'Asia/Dubai',
  DOH: 'Asia/Qatar',
  BAH: 'Asia/Bahrain',
  KWI: 'Asia/Kuwait',
  MCT: 'Asia/Muscat',
  RUH: 'Asia/Riyadh',
  JED: 'Asia/Riyadh',
  DMM: 'Asia/Riyadh',
  AMM: 'Asia/Amman',
  BEY: 'Asia/Beirut',
  CAI: 'Africa/Cairo',
  TLV: 'Asia/Jerusalem',

  // Asia — mostly no DST
  BOM: 'Asia/Kolkata',
  DEL: 'Asia/Kolkata',
  MAA: 'Asia/Kolkata',
  HYD: 'Asia/Kolkata',
  BLR: 'Asia/Kolkata',
  COK: 'Asia/Kolkata',
  KHI: 'Asia/Karachi',
  LHE: 'Asia/Karachi',
  CMB: 'Asia/Colombo',
  DAC: 'Asia/Dhaka',
  KTM: 'Asia/Kathmandu',
  MLE: 'Indian/Maldives',
  BKK: 'Asia/Bangkok',
  KUL: 'Asia/Kuala_Lumpur',
  SIN: 'Asia/Singapore',
  CGK: 'Asia/Jakarta',
  MNL: 'Asia/Manila',
  HKG: 'Asia/Hong_Kong',
  ICN: 'Asia/Seoul',
  NRT: 'Asia/Tokyo',
  HND: 'Asia/Tokyo',
  PVG: 'Asia/Shanghai',

  // Oceania
  SYD: 'Australia/Sydney',
  MEL: 'Australia/Melbourne',
  PER: 'Australia/Perth',
  AKL: 'Pacific/Auckland',

  // Africa
  JNB: 'Africa/Johannesburg',
  CPT: 'Africa/Johannesburg',
  NBO: 'Africa/Nairobi',
  ADD: 'Africa/Addis_Ababa',
  CMN: 'Africa/Casablanca',
  TNG: 'Africa/Casablanca',
  RAK: 'Africa/Casablanca',
  ALG: 'Africa/Algiers',

  // Indian Ocean
  MRU: 'Indian/Mauritius',
  RRG: 'Indian/Mauritius',
  RUN: 'Indian/Reunion',
  TNR: 'Indian/Antananarivo',
};

/** Standard UTC offset (hours) — DEPRECATED for display, use IANA zones instead.
 *  Kept for backwards compatibility with code that needs a numeric offset. */
const AIRPORT_UTC_OFFSET: Record<string, number> = {
  // Chile (standard = winter time UTC-4, but DST pushes to UTC-3)
  SCL: -4, ANF: -4, CJC: -4, IQQ: -4, LSC: -4, CCP: -4, PMC: -4,
  PUQ: -3, // Punta Arenas is permanent UTC-3
  IPC: -6, // Easter Island standard
  ARI: -4, CPO: -4, ZCO: -4, ZAL: -4, ZOS: -4, BBA: -3,
  // Peru
  LIM: -5, CUZ: -5, AQP: -5, TRU: -5, PIU: -5, IQT: -5,
  // Brazil
  GRU: -3, CGH: -3, GIG: -3, SDU: -3, BSB: -3, CNF: -3, SSA: -3, REC: -3,
  FOR: -3, POA: -3, CWB: -3, FLN: -3, NAT: -3, MAO: -4,
  // Colombia
  BOG: -5, MDE: -5, CLO: -5, CTG: -5, BAQ: -5,
  // Argentina
  EZE: -3, AEP: -3, COR: -3, MDZ: -3, BRC: -3,
  // Uruguay / Paraguay / Ecuador
  MVD: -3, ASU: -4, UIO: -5, GYE: -5,
  // Venezuela
  CCS: -4, VLN: -4,
  // Europe (standard offsets — DST handled by IANA zones)
  MAD: 1, BCN: 1, AGP: 1, PMI: 1, SVQ: 1, VLC: 1, BIO: 1, ALC: 1, SCQ: 1, VGO: 1, IBZ: 1,
  LPA: 0, TFN: 0, ACE: 0,
  LIS: 0, OPO: 0, FCO: 1, MXP: 1, NAP: 1, LHR: 0, LGW: 0, MAN: 0, EDI: 0, DUB: 0,
  CDG: 1, ORY: 1, FRA: 1, MUC: 1, DUS: 1, BER: 1, AMS: 1, BRU: 1, ZRH: 1, GVA: 1,
  VIE: 1, PRG: 1, WAW: 1, CPH: 1, ARN: 1, ATH: 2, IST: 3,
  // North America (summer offsets to match original behavior — DST handled by IANA zones)
  JFK: -4, LGA: -4, BOS: -4, PHL: -4, IAD: -4, DCA: -4, BWI: -4, CLT: -4, ATL: -4,
  MIA: -4, FLL: -4, TPA: -4, MCO: -4, JAX: -4, RSW: -4, PBI: -4, RDU: -4,
  DTW: -4, CLE: -4, PIT: -4, CMH: -4, IND: -4, PVD: -4, BDL: -4,
  ORD: -5, MDW: -5, DFW: -5, DAL: -5, HOU: -5, AUS: -5, SAT: -5, MSY: -5, STL: -5,
  MCI: -5, OKC: -5, BNA: -5, MSP: -5, MKE: -5,
  DEN: -6, SLC: -6, ABQ: -6, ELP: -6,
  PHX: -7, // Arizona, no DST
  LAX: -7, SFO: -7, SAN: -7, SJC: -7, OAK: -7, SMF: -7, BUR: -7, ONT: -7, SNA: -7,
  LAS: -7, SEA: -7, PDX: -7, RNO: -7,
  HHR: -8, CLD: -8, CEC: -8, MMH: -8, AXX: -7, SVC: -7, GUP: -7,
  HNL: -10, OGG: -10,
  SJU: -4,
  YYZ: -5, YVR: -8, YUL: -5,
  // Mexico
  MEX: -6, MTY: -6, CUN: -5, SJD: -7, PVR: -6,
  // Central America & Caribbean
  PTY: -5, SJO: -6, GUA: -6, SAL: -6, HAV: -5, SDQ: -4, MBJ: -5, NAS: -5,
  // Middle East
  AUH: 4, DXB: 4, DOH: 3, BAH: 3, KWI: 3, MCT: 4, RUH: 3, JED: 3, DMM: 3,
  AMM: 3, BEY: 3, CAI: 2, TLV: 2,
  // Asia
  BOM: 5.5, DEL: 5.5, MAA: 5.5, HYD: 5.5, BLR: 5.5, COK: 5.5,
  KHI: 5, LHE: 5, CMB: 5.5, DAC: 6, KTM: 5.75, MLE: 5,
  BKK: 7, KUL: 8, SIN: 8, CGK: 7, MNL: 8, HKG: 8, ICN: 9, NRT: 9, PVG: 8,
  // Oceania
  SYD: 10, MEL: 10, PER: 8, AKL: 12,
  // Africa
  JNB: 2, CPT: 2, NBO: 3, ADD: 3, CMN: 1, TNG: 1, RAK: 1, ALG: 1,
  // Indian Ocean
  MRU: 4, RRG: 4, RUN: 4, TNR: 3,
};

/** Pilot's primary region offset when an airport isn't mapped. */
const DEFAULT_UTC_OFFSET = -3;
/** Default IANA zone (South American pilot region). */
const DEFAULT_IANA_ZONE = 'America/Sao_Paulo';

/**
 * Get the IANA timezone identifier for an airport.
 * Falls back to America/Sao_Paulo (pilot region) for unknown airports.
 */
export function airportIanaZone(iata?: string | null): string {
  if (!iata) return DEFAULT_IANA_ZONE;
  return AIRPORT_IANA_ZONE[iata.toUpperCase()] ?? DEFAULT_IANA_ZONE;
}

/**
 * Standard UTC offset in hours for an airport. DEPRECATED for display purposes.
 * Use the format functions (formatFlightClock, etc.) which handle DST correctly.
 * This returns the STANDARD offset (winter for southern hemisphere, summer for
 * northern hemisphere) and does NOT account for DST transitions.
 */
export function airportUtcOffset(iata?: string | null): number {
  if (!iata) return DEFAULT_UTC_OFFSET;
  return AIRPORT_UTC_OFFSET[iata.toUpperCase()] ?? DEFAULT_UTC_OFFSET;
}

/**
 * Format an instant in the airport's local timezone using IANA zones.
 * This correctly handles DST transitions.
 */
function formatInAirportTz(
  iso: string | null | undefined,
  airportIata: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';

  const tz = airportIanaZone(airportIata);
  try {
    return new Date(t).toLocaleString(undefined, { timeZone: tz, ...options });
  } catch {
    // Fallback for environments without IANA tz support (very rare)
    const shifted = new Date(t + airportUtcOffset(airportIata) * 3_600_000);
    return shifted.toLocaleString(undefined, { timeZone: 'UTC', ...options });
  }
}

/** Full flight datetime in the departure airport's local time — "03 Jun 2026, 11:35 PM". */
export function formatFlightDateTime(
  iso: string | null | undefined,
  airportIata?: string | null,
): string {
  return formatInAirportTz(iso, airportIata, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Short flight time in the relevant airport's local time — "Jun 3, 11:35 PM". */
export function formatFlightTime(
  iso: string | null | undefined,
  airportIata?: string | null,
): string {
  return formatInAirportTz(iso, airportIata, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Wall-clock only, airport-local — "11:35 PM". For departure-board rows where the
 *  day is carried separately (see `formatFlightDay`) so the time can be set big. */
export function formatFlightClock(
  iso: string | null | undefined,
  airportIata?: string | null,
): string {
  return formatInAirportTz(iso, airportIata, { hour: '2-digit', minute: '2-digit' });
}

/** 24-hour wall clock, airport-local — "13:31". The departures board reads in
 *  24 h; a hero that restates one of its rows must not flip to "01:31 PM". */
export function formatFlightClock24(
  iso: string | null | undefined,
  airportIata?: string | null,
): string {
  return formatInAirportTz(iso, airportIata, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Weekday + calendar day, airport-local — "Wed, Jun 3". For the one place a
 *  wrong-day pick is expensive (the express confirmation hero). */
export function formatFlightWeekdayDay(
  iso: string | null | undefined,
  airportIata?: string | null,
): string {
  return formatInAirportTz(iso, airportIata, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** Calendar day only, airport-local — "Jun 3". Pairs with `formatFlightClock`. */
export function formatFlightDay(
  iso: string | null | undefined,
  airportIata?: string | null,
): string {
  return formatInAirportTz(iso, airportIata, { month: 'short', day: 'numeric' });
}

/**
 * Format a ground operation time (shuttle pickup, check-in, etc.) in the
 * disruption airport's timezone — "14:30".
 *
 * Ground operations happen at a specific airport (the disruption's hub), so
 * times should display in that airport's local time, not the browser's.
 *
 * If no airport is provided, falls back to UTC (not browser time) to avoid
 * showing a time that could be hours off from the actual local time.
 */
export function formatGroundTime(
  iso: string | null | undefined,
  airportIata?: string | null,
): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';

  // If no airport, use UTC to avoid incorrect browser-local times
  if (!airportIata) {
    const d = new Date(t);
    const h = String(d.getUTCHours()).padStart(2, '0');
    const m = String(d.getUTCMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  return formatInAirportTz(iso, airportIata, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Hour bucket for the flights board separator rows — "18 Aug · 23:00 LIM".
 * Uses the departure airport's timezone so the bucket matches the row times.
 * Issue #662: Without this, a LIM flight at 23:50 local (04:50Z) would show
 * under "Oct 09 · 01:00" in a Buenos Aires browser instead of "Oct 08 · 23:00".
 *
 * The airport code is appended to avoid confusion when the board mixes flights
 * from different timezones (rows are sorted by UTC, so local hour buckets can
 * appear out of order: "23:00 LIM", "02:00 GRU", "00:00 LIM"). The code makes
 * each bucket unambiguous.
 *
 * If no airport is provided, formats in UTC with "UTC" label instead of
 * silently defaulting to any region.
 */
export function formatFlightHourBucket(
  iso: string | null | undefined,
  airportIata?: string | null,
): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;

  // If no airport, use UTC explicitly labeled
  if (!airportIata) {
    const d = new Date(t);
    const date = d.toLocaleString(undefined, {
      timeZone: 'UTC',
      day: '2-digit',
      month: 'short',
    });
    const hourOnly = String(d.getUTCHours()).padStart(2, '0') + ':00';
    return `${date} · ${hourOnly} UTC`;
  }

  const tz = airportIanaZone(airportIata);
  const code = airportIata.toUpperCase();
  try {
    const date = new Date(t).toLocaleString(undefined, {
      timeZone: tz,
      day: '2-digit',
      month: 'short',
    });
    const hour = new Date(t).toLocaleString(undefined, {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    // Round to the hour bucket (e.g., 23:50 → 23:00)
    const hourOnly = hour.slice(0, 2) + ':00';
    return `${date} · ${hourOnly} ${code}`;
  } catch {
    // Fallback for environments without IANA tz support
    const shifted = new Date(t + airportUtcOffset(airportIata) * 3_600_000);
    const date = shifted.toLocaleString(undefined, {
      timeZone: 'UTC',
      day: '2-digit',
      month: 'short',
    });
    const hourOnly = String(shifted.getUTCHours()).padStart(2, '0') + ':00';
    return `${date} · ${hourOnly} ${code}`;
  }
}

/* ── The OPERATOR's clock (the browser's zone) ─────────────────────────────────
 * PO 2026-10-28: «mostrar ambas horas en el listado — Hora local, Hora origen — y que
 * los cálculos los haga basado en el horario del operador». The airport-local helpers
 * above stay the «Hora origen»; these are the «Hora local»: the zone the operator's
 * own computer is in, so a board mixing countries reads in ONE clock, in order. */

/** 24-hour wall clock in the operator's own zone — "14:32". */
export function formatOperatorClock24(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  return new Date(t).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** The hour bucket in the operator's own zone — "28 oct · 14:00" (no airport code: one
 *  clock for every row, so the buckets follow the rows' real order). */
export function formatOperatorHourBucket(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  const date = d.toLocaleString(undefined, { day: '2-digit', month: 'short' });
  const hour = d.toLocaleString(undefined, { hour: '2-digit', hour12: false });
  return `${date} · ${hour.padStart(2, '0').slice(0, 2)}:00`;
}
